/**
 * What the things inside a Property are called, on a screen that is not looking at one Property.
 *
 * **The vocabulary, and the collision it created.** In the development module the scheme itself is
 * a *Property* — the same word a letting agency uses for a building — and the things sold inside
 * it are plots, homes or units according to that Property's own `sells` setting. `soldNoun()` in
 * `packages/core/src/development.ts` is the one place that decision is made, and every screen in
 * the module asks it before writing a heading. Where a screen has one Property in hand, the server
 * answers for it: `GET /estates/:id` returns `noun` beside the record, and that is always the
 * better source because it is the scheme's own word.
 *
 * **This is for the screens that do not.** The purchases list shows every sale the agency has
 * made, across every scheme; one column heading has to serve all of them. Calling that column
 * "Property" is the one thing it cannot be called, because the column beside it is the Property.
 *
 * So the word is agreed across the whole account: **where every Property sells the same thing, its
 * word is used; where they disagree, "unit" is used**, which is `soldNoun`'s own fallback and the
 * only one of the three that is not actively wrong for the others. A developer selling nothing but
 * land reads "Plot" everywhere, which is the case this exists for; one selling land beside a
 * terrace of houses reads "Unit", which is the honest answer rather than a guess.
 *
 * The request is the picker the sale form already makes — same key, same URL, so the two share one
 * response rather than asking twice. It is deliberately allowed to fail: a member who may see
 * purchases but not properties gets a refusal here, and the fallback noun is a better outcome than
 * a screen that will not draw over a column heading.
 */

import { useQuery } from "@tanstack/react-query";
import { soldNoun } from "@rentorng/core";
import { api } from "@/lib/api";

export interface PropertyChoice {
  id: string;
  name: string;
  /** Which of plot / home / unit this scheme's contents are called. */
  sells: string;
}

/**
 * Every Property, for a picker or for the vocabulary.
 *
 * Two hundred is not a page: it is "all of them" for any account this control makes sense on, and
 * a scheme missing from a picker is a sale that cannot be recorded.
 */
export function usePropertyChoices() {
  return useQuery({
    queryKey: ["estates", "picker"],
    queryFn: () => api.get<{ estates: PropertyChoice[] }>("/estates?perPage=200"),
    staleTime: 60_000,
    retry: false,
  });
}

/**
 * The noun for the things sold inside a Property.
 *
 * Pass the Property's own `sells` where the screen knows it — that answer is exact, and no request
 * is needed for it. With nothing passed, the account-wide agreement above is used.
 */
export function useSoldNoun(sells?: string | null): { one: string; many: string } {
  const { data } = usePropertyChoices();
  if (sells) return soldNoun(sells);

  const words = new Set((data?.estates ?? []).map((property) => property.sells));
  return words.size === 1 ? soldNoun([...words][0]) : soldNoun(null);
}

/**
 * The noun for one particular Property, on a screen that holds only its id.
 *
 * `GET /estates/:id` and `GET /estates/:id/tree` both answer `noun` beside the record, and where a
 * screen has that answer it is always the better one — it is the scheme's own word rather than an
 * agreement across the account. But a purchase carries an estate id and nothing else, and its
 * statement has to name what was bought before either of those requests would be worth making.
 *
 * So the picker the module already loads is read for that one scheme's `sells`, and the account-wide
 * agreement is the fallback for the moment before it lands — or for a member who may see purchases
 * but not properties, whose picker is refused and who should still get a word rather than a gap.
 */
export function usePropertyNoun(estateId?: string | null): { one: string; many: string } {
  const { data } = usePropertyChoices();
  const property = estateId ? (data?.estates ?? []).find((row) => row.id === estateId) : undefined;
  const words = new Set((data?.estates ?? []).map((row) => row.sells));
  if (property) return soldNoun(property.sells);
  return words.size === 1 ? soldNoun([...words][0]) : soldNoun(null);
}

/* ------------------------------------------------------------ commission rates */

/**
 * A rate, written the way somebody says it out loud.
 *
 * Stored in basis points for the same reason money is stored in minor units — 2.5% is 250, never
 * 0.025000000000000001 — and shown as a percentage, because nobody negotiates in basis points.
 * Trailing zeros are dropped so 5% is "5%" rather than "5.00%", and a deliberate zero is "0%",
 * which is a decision somebody took and not the same as nothing having been said.
 */
export function commissionLabel(bp: number | null | undefined): string | null {
  if (bp === null || bp === undefined) return null;
  const percent = bp / 100;
  return `${Number(percent.toFixed(2))}%`;
}

/** A percentage typed into a box, as basis points. Null for an empty box — "nobody has said". */
export function commissionBpFrom(input: string | null | undefined): number | null {
  const text = (input ?? "").trim();
  if (text === "") return null;
  const percent = Number(text);
  if (!Number.isFinite(percent)) return null;
  return Math.round(percent * 100);
}

/** The other way, for filling a box with what is already stored. */
export function commissionPercentValue(bp: number | null | undefined): string {
  if (bp === null || bp === undefined) return "";
  return String(Number((bp / 100).toFixed(2)));
}

/**
 * Where a rate came from, in words a screen can put beside it.
 *
 * A rate that was inherited and a rate that was set here look identical as a number, and the
 * difference decides what happens when somebody changes the level above. Saying which it is turns
 * "3%" into "3%, from the price list" — which is the sentence that stops a developer overriding a
 * rate they only meant to read.
 */
export const COMMISSION_SOURCE_LABEL: Record<string, string> = {
  node: "set here",
  type: "from the price list",
  phase: "from the phase",
  estate: "from the property",
};
